// src/components/admin/AdminDashboard.tsx
// Updated to add tuition payment and evaluation statistics
// Updated to add search filter, stabilize Firestore listener, and enhance admin usability
// Updated to add search filter, refine case-insensitive role filtering, and enhance admin usability
// Updated to add search filter, refine case-insensitive role filtering, and enhance admin usability
// Updated to fix JSX syntax (missing > in Col tags), add search filter, and enhance admin usability

import React, { useEffect, useState, useMemo } from "react";
import { withFirebase, FirebaseContextType } from "../dev/Firebase/Firebase";
import { Card, Statistic, Row, Col, Select, Input } from "antd";
import styled from "styled-components";
import { userDocToUser } from "../dev/utils/userHelper";
import { collection, onSnapshot, query } from "firebase/firestore";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";

const { Option } = Select;
const { Search } = Input;

const DashboardContainer = styled.div`
  padding: 24px;
  background: linear-gradient(to bottom, #fff5e6, #fff);
  box-shadow: 0 4px 15px rgba(255, 204, 0, 0.2);
  border-radius: 8px;
  animation: fadeIn 1.5s ease-in;

  @media (max-width: 768px) {
    padding: 10px;
    .ant-card {
      font-size: 14px;
    }
  }
`;

const ClickableCard = styled(Card)`
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  cursor: pointer;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 8px 20px rgba(255, 204, 0, 0.4);
  }
`;

const FilterContainer = styled.div`
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

const FilterLabel = styled.span`
  margin-right: 8px;
  font-weight: bold;
  color: #0427f4;
`;

const SearchWrapper = styled.div`
  max-width: 300px;
  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

interface User {
  key: string;
  userRole?: string;
  verificationStatus: string;
  submittedForVerification: boolean;
  isDisabled: boolean;
  evaluationStatus?: string;
  tuitionFees?: {
    introPaymentStatus?: "Pending" | "Completed";
    initiPaymentStatus?: "Pending" | "Completed";
  };
  name?: string;
  email?: string;
  [key: string]: any;
}

interface AdminDashboardProps {
  firebase: FirebaseContextType;
}

const AdminDashboard: React.FC<AdminDashboardProps> = ({ firebase }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "disabled">("all");
  const [userTypeFilter, setUserTypeFilter] = useState<"all" | "uOLSS" | "uOLSME" | "uOLSLM">("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    console.log("AdminDashboard rendering - loading state");
    if (!firebase.firestore) {
      console.error("AdminDashboard: Firestore instance not initialized");
      setError("Firestore instance not initialized");
      setLoading(false);
      return;
    }
    const usersQuery = query(collection(firebase.firestore, "users"));
    const unsubscribe = onSnapshot(usersQuery, (snapshot) => {
      const usersList: User[] = snapshot.docs.map((doc) => userDocToUser(doc));
      console.log("Fetched users for dashboard:", usersList);
      setUsers(usersList);
      setLoading(false);
    }, (err) => {
      console.error("Error fetching users:", err);
      setError("Failed to fetch users: " + err.message);
      setLoading(false);
    });

    return () => {
      console.log("Cleaning up Firestore listener");
      unsubscribe();
    };
  }, [firebase]);

  useEffect(() => {
    let filtered: User[] = users;

    // Apply status filter
    if (statusFilter === "active") {
      filtered = filtered.filter((user) => !user.isDisabled);
    } else if (statusFilter === "disabled") {
      filtered = filtered.filter((user) => user.isDisabled);
    }

    // Apply user type filter (case-insensitive)
    if (userTypeFilter !== "all") {
      const normalizedFilter = userTypeFilter.toLowerCase();
      filtered = filtered.filter((user) => {
        const userRole = user.userRole?.toLowerCase();
        if (normalizedFilter === "uolss")
          return userRole === "uolss" || userRole === "uolss_col";
        if (normalizedFilter === "uolsme")
          return userRole === "uolsme" || userRole === "olsme_col";
        if (normalizedFilter === "uolslm")
          return userRole === "uolslm" || userRole === "olslm_col";
        return false;
      });
    }

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter((user) =>
        `${user.name || ""} ${user.email || ""}`
          .toLowerCase()
          .includes(searchQuery.toLowerCase())
      );
    }

    setFilteredUsers(filtered);
    console.log("Filtered users after applying filters:", {
      statusFilter,
      userTypeFilter,
      searchQuery,
      filtered,
    });
  }, [users, statusFilter, userTypeFilter, searchQuery]);

  if (loading) {
    return <div>Loading dashboard...</div>;
  }

  if (error) {
    console.log("AdminDashboard rendering - error state:", error);
    return <div style={{ color: "red", padding: 24 }}>{error}</div>;
  }

  const totalUsers = filteredUsers.length;
  const activeUsers = filteredUsers.filter((user) => !user.isDisabled).length;
  const pendingVerification = filteredUsers.filter(
    (user) => user.submittedForVerification
  ).length;
  const pendingTuitionPayments = filteredUsers.filter(
    (user) =>
      user.tuitionFees?.introPaymentStatus === "Pending" ||
      user.tuitionFees?.initiPaymentStatus === "Pending"
  ).length;
  const completedTuitionPayments = filteredUsers.filter(
    (user) =>
      user.tuitionFees?.introPaymentStatus === "Completed" &&
      user.tuitionFees?.initiPaymentStatus === "Completed"
  ).length;
  const pendingEvaluation = filteredUsers.filter(
    (user) => user.evaluationStatus !== "Evaluated"
  ).length;

  const handleCardClick = (
    filter:
      | "all"
      | "active"
      | "pending"
      | "pendingTuition"
      | "completedTuition"
      | "pendingEvaluation"
  ) => {
    navigate(`/admin/users/verify?filter=${filter}`);
  };

  return (
    <DashboardContainer>
      <Helmet>
        <title>OLS Admin Dashboard - Sun Light Meditation</title>
        <meta
          name="description"
          content="Manage OLS users, payments, and evaluations. Join OLS at olsme.com/OLSStudentProspect."
        />
        <meta
          name="keywords"
          content="OLS admin dashboard, Sun Light Meditation, user management, join OLS"
        />
      </Helmet>
      <FilterContainer>
        <div>
          <FilterLabel>Filter by status:</FilterLabel>
          <Select
            value={statusFilter}
            onChange={(value: "all" | "active" | "disabled") =>
              setStatusFilter(value)
            }
            style={{ width: 200 }}
            aria-label="Filter users by status"
          >
            <Option value="all">All users</Option>
            <Option value="active">Active users</Option>
            <Option value="disabled">Disabled users</Option>
          </Select>
        </div>
        <div>
          <FilterLabel>Filter by user type:</FilterLabel>
          <Select
            value={userTypeFilter}
            onChange={(value: "all" | "uOLSS" | "uOLSME" | "uOLSLM") =>
              setUserTypeFilter(value)
            }
            style={{ width: 200 }}
            aria-label="Filter users by type"
          >
            <Option value="all">All types</Option>
            <Option value="uOLSS">uOLSS</Option>
            <Option value="uOLSME">uOLSME</Option>
            <Option value="uOLSLM">uOLSLM</Option>
          </Select>
        </div>
        <SearchWrapper>
          <FilterLabel>Search users:</FilterLabel>
          <Search
            placeholder="Search by name or email"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            allowClear
            aria-label="Search users by name or email"
          />
        </SearchWrapper>
      </FilterContainer>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={8}>
          <ClickableCard onClick={() => handleCardClick("all")}>
            <Statistic title="Total Users" value={totalUsers} />
          </ClickableCard>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <ClickableCard onClick={() => handleCardClick("active")}>
            <Statistic title="Active Users" value={activeUsers} />
          </ClickableCard>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <ClickableCard onClick={() => handleCardClick("pending")}>
            <Statistic title="Pending Verification" value={pendingVerification} />
          </ClickableCard>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <ClickableCard onClick={() => handleCardClick("pendingTuition")}>
            <Statistic title="Pending Tuition Payments" value={pendingTuitionPayments} />
          </ClickableCard>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <ClickableCard onClick={() => handleCardClick("completedTuition")}>
            <Statistic title="Completed Tuition Payments" value={completedTuitionPayments} />
          </ClickableCard>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <ClickableCard onClick={() => handleCardClick("pendingEvaluation")}>
            <Statistic title="Pending Evaluation" value={pendingEvaluation} />
          </ClickableCard>
        </Col>
      </Row>
    </DashboardContainer>
  );
};

export default withFirebase(AdminDashboard);